import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PendingUpdateNotification from '@/components/PendingUpdateNotification';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Edit, 
  Star, 
  Clock, 
  MapPin, 
  GraduationCap, 
  Languages,
  DollarSign,
  Calendar,
  MessageCircle,
  Heart,
  BookOpen,
  Video,
  CalendarPlus,
  Users,
  Upload,
  Link as LinkIcon,
  Play,
  AlertTriangle,
  Camera,
  Map,
  Copy,
  Lock,
  ArrowRight,
  Bell
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTutorProfile } from '@/hooks/useTutorProfile';
import { useTutors } from '@/hooks/useTutors';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTutorStatus } from '@/hooks/useTutorStatus';
import { useTutorProfileApproval } from '@/hooks/useTutorProfileApproval';
import { useTutorPendingStatus } from '@/hooks/useTutorPendingStatus';
import { useLessons } from '@/hooks/useLessons';
import TutorProfileTabs from '@/components/TutorProfileTabs';
import EditProfileModal from '@/components/EditProfileModal';
import ProfileUpdateSuccessModal from '@/components/ProfileUpdateSuccessModal';
import ScheduleSessionModal from '@/components/ScheduleSessionModal';
import PendingProfileRequestModal from '@/components/PendingProfileRequestModal';
import { PendingProfileSubmissionModal } from '@/components/PendingProfileSubmissionModal';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import ProfileSuspensionModal from '@/components/ProfileSuspensionModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import TutorCountryFlag from '@/components/TutorCountryFlag';
import TutorReviews from '@/components/TutorReviews';
import { useInstantSession } from '@/hooks/useInstantSession';
import { useInstantSessionApproval } from '@/hooks/useInstantSessionApproval';
import { pb } from '@/lib/pocketbase';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import { convertToEmbeddableUrl } from '@/lib/utils';
import { useReviews } from '@/hooks/useReviews';
import AddTravelStoryModal from '@/components/AddTravelStoryModal';
import { PostInputSection } from '@/components/PostInputSection';
import { LearningPlanModal } from '@/components/tutor/LearningPlanModal';



const TutorProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { 
    profile, 
    isLoading: profileLoading, 
    uploadProfilePicture, 
    updateProfilePictureUrl,
    getAllAvailableSubjects,
    approvedSubjects
  } = useTutorProfile();
  const { tutors, isLoading: tutorsLoading } = useTutors();
  const { isPendingTutor, hasAccessToTutorFeatures } = useTutorStatus();
  const { toast } = useToast();
  const { createInstantSession, createScheduledSession, isCreating } = useInstantSession();
  const { isApproved: isInstantSessionApproved } = useInstantSessionApproval();
  const { hasActivePendingUpdate, isInitialLoad, checkPendingUpdates } = useTutorProfileApproval();
  const { 
    hasPendingProfileSubmission, 
    hasPendingProfileUpdate, 
    isLoading: pendingStatusLoading,
    pendingSubmissionDate,
    pendingUpdateDate
  } = useTutorPendingStatus();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isAddStoryModalOpen, setIsAddStoryModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isSuspensionModalOpen, setIsSuspensionModalOpen] = useState(false);
  const [isLearningPlanOpen, setIsLearningPlanOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [tutorData, setTutorData] = useState<any>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showProfilePictureSection, setShowProfilePictureSection] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [skipNextRefresh, setSkipNextRefresh] = useState(false);
  const [showPendingRequestModal, setShowPendingRequestModal] = useState(false);
  const [pendingRequestType, setPendingRequestType] = useState<'create' | 'edit'>('edit');
  const [showPendingSubmissionModal, setShowPendingSubmissionModal] = useState(false);
  const [pendingSubmissionType, setPendingSubmissionType] = useState<'initial' | 'update'>('initial');
  const [scrollToSection, setScrollToSection] = useState<string | undefined>(undefined);
  
  // Add useReviews hook for dynamic review data
  const { reviews, calculateAverageRating } = useReviews(tutorData?.id);
  
  // Get lessons data for the tabs
  const { lessons } = useLessons(tutorData?.user);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  // Function to manually fetch fresh profile data with proper abort handling
  const fetchFreshProfile = async (userId: string) => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      
      if (signal.aborted) {
        return null;
      }
      
      const records = await pb.collection('tutors').getList(1, 1, {
        filter: `user = "${userId}"`,
        expand: 'user'
      });
      
      if (signal.aborted) {
        return null;
      }
      
      if (records.items.length > 0) {
        const freshProfile = records.items[0];
        setTutorData(freshProfile);
        return freshProfile;
      } else {
        // If no profile exists for current user and they're a tutor, show a message to create one
        if (user && user.role === 'tutor' && user.id === userId) {
          setTutorData({
            id: null,
            user: userId,
            name: user.name || 'New Tutor',
            bio: '',
            shortBio: '',
            subjects: [],
            hourlyRate: 0,
            pricingType: null,
            sessionRate: 0,
            levelRates: { beginner: 0, intermediate: 0, advanced: 0 },
            monthlyRate: 0,
            languages: [],
            experience: 'Not specified',
            education: [],
            availability: { days: [], timeSlots: [] },
            rating: 0,
            reviewCount: 0,
            profilePicture: '',
            profilePictureUrl: '',
            country: '',
            sessionPreference: '',
            paymentMethods: []
          });
        }
      }
    } catch (error: any) {
      if (error?.message?.includes('autocancelled') || error?.name === 'AbortError' || signal.aborted) {
        return null;
      }
    }
    return null;
  };

  // Enhanced profile refresh function
  const refreshProfile = async () => {
    // Determine which user ID to use
    let targetUserId: string | null = null;
    if (!id && user?.role === 'tutor') {
      targetUserId = user.id;
    } else if (id && user && user.id === id && user.role === 'tutor') {
      targetUserId = user.id;
    }
    
    if (targetUserId) {
      await fetchFreshProfile(targetUserId);
    } else if (profile) {
      setTutorData(profile);
    }
    
    // Force a re-render
    setRefreshKey(prev => prev + 1);
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Check auto-approval settings for pending tutors with aggressive refresh AND auto-trigger approval
  useEffect(() => {
    const checkAutoApproval = async () => {
      // Only check auto-approval for pending tutors
      if (isPendingTutor || user?.status === 'pending') {
        try {
          const adminSettings = await pb.collection('admin_settings').getFirstListItem('');
          const isEnabled = adminSettings.auto_approve_tutors || false;
          setAutoApprovalEnabled(isEnabled);
          
          console.log('🔍 TutorProfile: Auto-approval check', {
            isEnabled,
            userStatus: user?.status,
            userId: user?.id
          });
          
          // If auto-approval is enabled and user is still pending, TRIGGER AUTO-APPROVAL NOW
          if (isEnabled && user?.status === 'pending' && user?.id) {
            console.log('🤖 [FALLBACK AUTO-APPROVAL] Triggering auto-approval for pending tutor on profile page load');
            
            try {
              // Use the robust auto-approval system
              const { robustAutoApprove } = await import('@/utils/robustAutoApproval');
              const result = await robustAutoApprove(user.id);
              
              if (result.success) {
                console.log('✅ [FALLBACK AUTO-APPROVAL] Success! Refreshing state...');
                
                // Refresh profile data instead of full page reload
                if (user?.id) {
                  await fetchFreshProfile(user.id);
                }
                setRefreshKey(prev => prev + 1);
              } else {
                console.error('❌ [FALLBACK AUTO-APPROVAL] Failed:', result.error);
                // Still check for status mismatch as fallback
                const freshUser = await pb.collection('users').getOne(user.id);
                console.log('📊 Fresh user status from database:', freshUser.status);
                
                if (freshUser.status === 'active' && user?.status === 'pending') {
                  console.log('⚠️ Status mismatch detected! Database: active, React: pending');
                  console.log('🔄 Forcing page refresh to sync state...');
                  window.location.reload();
                }
              }
            } catch (error) {
              console.error('❌ [FALLBACK AUTO-APPROVAL] Error:', error);
              
              // Still check for status mismatch as fallback
              try {
                const freshUser = await pb.collection('users').getOne(user.id);
                console.log('📊 Fresh user status from database:', freshUser.status);
                
                if (freshUser.status === 'active' && user?.status === 'pending') {
                  console.log('⚠️ Status mismatch detected! Database: active, React: pending');
                  console.log('🔄 Forcing page refresh to sync state...');
                  window.location.reload();
                }
              } catch (verifyError) {
                console.error('Error verifying user status:', verifyError);
              }
            }
          }
        } catch (error) {
          console.error('Error checking auto-approval settings:', error);
          setAutoApprovalEnabled(false);
        }
      }
    };

    checkAutoApproval();
  }, [isPendingTutor, user?.status, user?.role, user?.id, toast]);

  // Handle successful profile update - refresh pending status
  const handleProfileUpdateSuccess = async () => {
    // Refresh all pending status checks immediately
    await Promise.all([
      checkPendingUpdates?.(),
      // Force refresh profile data
      refreshProfile()
    ]);
    
    // Show success modal
    setShowSuccessModal(true);
  };
  const showPendingNotification = (type: 'create' | 'edit') => {
    setPendingRequestType(type);
    setShowPendingRequestModal(true);
  };

  // Show pending submission notification
  const showPendingSubmissionNotification = (type: 'initial' | 'update') => {
    setPendingSubmissionType(type);
    setShowPendingSubmissionModal(true);
  };

  // Check for pending submissions before allowing profile creation
  const handleCreateProfileClick = () => {
    if (hasPendingProfileSubmission) {
      toast({
        title: "Profile Pending Review",
        description: "Your profile submission is being reviewed by our administrators. You'll be notified once it's approved.",
        variant: "default"
      });
      return;
    }

    if (hasPendingProfileUpdate || hasActivePendingUpdate === true) {
      showPendingNotification('create');
      return;
    }
    setIsEditModalOpen(true);
  };

  // Function to create tutor profile with auto-approval logic
  const handleStartProfile = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not found. Please refresh the page and try again.",
        variant: "destructive"
      });
      return;
    }

    // Check for pending submissions before proceeding
    if (hasPendingProfileSubmission) {
      showPendingSubmissionNotification('initial');
      return;
    }

    setIsCreatingProfile(true);
    
    try {
      setSkipNextRefresh(true);
      
      
      
      // Use auto-approval logic
      const { autoApproveIfEnabled } = await import('@/utils/checkAutoApprovalSettings');
      const wasAutoApproved = await autoApproveIfEnabled(user.id);
      
      if (wasAutoApproved) {
        // Refresh profile data
        await fetchFreshProfile(user.id);
        setIsEditModalOpen(true);
        
      } else {
        // Profile creation submitted for manual review
        toast({
          title: "Profile Submitted!",
          description: "Your profile has been submitted for admin review. You'll be notified once approved.",
        });
      }

    } catch (error: any) {
      console.error('Error creating tutor profile:', error);
      
      // Extract meaningful error message
      let errorMessage = "Failed to create profile. Please try again.";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      
      toast({
        title: "Error Creating Profile",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsCreatingProfile(false);
      setTimeout(() => setSkipNextRefresh(false), 2000);
    }
  };

  // Main data loading effect - optimized to prevent cascading triggers
  useEffect(() => {
    const loadTutorData = async () => {
      // Skip refresh if we're in the middle of profile creation
      if (skipNextRefresh) {
        return;
      }
      
      // Mark that we're attempting to load data
      setDataLoaded(false);
      
      // If no specific ID provided, use current user's profile (only if they're a tutor)
      if (!id) {
        if (user?.role === 'tutor') {
          await fetchFreshProfile(user.id);
          setDataLoaded(true);
          return;
        } else {
          setTutorData(null);
          setDataLoaded(true);
          return;
        }
      }

      // If ID is the current user's ID and they're a tutor
      if (id && user && user.id === id && user.role === 'tutor') {
        await fetchFreshProfile(user.id);
        setDataLoaded(true);
        return;
      }

      // For specific tutor ID, first try to find in the tutors list
      if (id && tutors && tutors.length > 0) {
        const tutor = tutors.find(t => t.id === id || t.user === id);
        if (tutor) {
          setTutorData(tutor);
          setDataLoaded(true);
          return;
        }
      }

      // If not found in tutors list and we have an ID, fetch directly from database
      if (id && !profileLoading && !tutorsLoading) {
        try {
          const fetchedProfile = await fetchFreshProfile(id);
          if (fetchedProfile) {
            setDataLoaded(true);
            return;
          }
        } catch (error) {
          
        }
        
        setTutorData(null);
        setDataLoaded(true);
      }
    };

    loadTutorData();
  }, [id, user?.id, user?.role, refreshKey]);

  const isOwnProfile = user && tutorData && (user.id === tutorData.user || user.id === tutorData.id);
  const isStudent = user?.role === 'student';
  const isTutor = user?.role === 'tutor';

  // Calculate dynamic review values
  const averageRating = calculateAverageRating();
  const reviewCount = reviews.length;

  const handleStartInstantSession = async () => {
    const result = await createInstantSession();
    // Navigation is handled inside the createInstantSession function
  };

  const handleProfileSave = async () => {
    // Set flag to prevent page refresh during profile save
    setSkipNextRefresh(true);
    
    // Reset the flag after a delay - no need to refresh since the data is already updated
    setTimeout(() => setSkipNextRefresh(false), 1000);
  };

  const handleEditSection = (section: string) => {
    setScrollToSection(section);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setScrollToSection(undefined);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    setIsEditModalOpen(false); // now close the edit modal only after user clicks close
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingPicture(true);
    try {
      await uploadProfilePicture(file);
      await refreshProfile();
      setShowProfilePictureSection(false);
    } catch (error) {
      
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!profilePictureUrl.trim()) return;

    setIsUploadingPicture(true);
    try {
      await updateProfilePictureUrl(profilePictureUrl);
      await refreshProfile();
      setShowProfilePictureSection(false);
      setProfilePictureUrl('');
    } catch (error) {
      
    } finally {
      setIsUploadingPicture(false);
    }
  };

  // Helper function to safely render experience
  const renderExperience = (experience: any) => {
    if (!experience) return null;
    
    if (typeof experience === 'string') {
      return experience;
    }
    
    if (typeof experience === 'object' && experience.description) {
      return `${experience.years || 0} years - ${experience.description}`;
    }
    
    return 'Experience details available';
  };

  // Helper function to extract video ID from YouTube URL (for backward compatibility)
  // Also supports converting Google Drive and other video URLs
  const getYouTubeVideoId = (url: string) => {
    // Use the shared utility to get embeddable URL
    const embedUrl = convertToEmbeddableUrl(url);
    
    // Extract video ID from YouTube embed URL for backward compatibility
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = embedUrl.match(regex);
    return match ? match[1] : null;
  };

  // Show loading while data is being fetched (but not during profile creation)
  if ((!dataLoaded || profileLoading || tutorsLoading) && !skipNextRefresh) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start space-x-6">
              <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show not found only after data loading is complete and no data exists
  if (dataLoaded && !tutorData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <p className="text-gray-600">The tutor profile you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Show only waiting message for pending tutors on their own profile (but not when modal is open)
  if (isOwnProfile && isTutor && (isPendingTutor || user?.status === 'pending') && !isEditModalOpen) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4">
                <Clock className="h-16 w-16 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl text-yellow-800">
                {autoApprovalEnabled ? "Ready to Start Your Profile?" : "Waiting for Admin Approval"}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              {autoApprovalEnabled ? (
                <>
                  <p className="text-yellow-700 text-lg">
                    Auto-approval is enabled! You can start creating your tutor profile right away.
                  </p>
                   <Button 
                     onClick={handleStartProfile}
                     disabled={isCreatingProfile || hasPendingProfileSubmission || hasPendingProfileUpdate}
                     size="lg"
                     className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                   >
                    {isCreatingProfile ? (
                      <>
                        <div className="animate-spin h-5 w-5 mr-3 border-2 border-white border-t-transparent rounded-full"></div>
                        Creating Profile...
                      </>
                    ) : (
                       <>
                         <Edit className="w-6 h-6 mr-3" />
                         {hasPendingProfileSubmission ? 'Profile Pending Approval' : 
                          hasPendingProfileUpdate ? 'Update Pending' : 
                          'Start Your Profile'}
                       </>
                    )}
                  </Button>
                  <p className="text-sm text-yellow-600">
                    This will create your tutor profile and open the editing form.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-yellow-700 text-lg">
                    Your tutor application is currently under review. You'll gain access to all tutor features once approved by our admin team.
                  </p>
                  <p className="text-sm text-yellow-600">
                    This process typically takes 1-2 business days.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentProfilePictureUrl = getProfilePictureUrl(tutorData);
  const allAvailableSubjects = getAllAvailableSubjects();

  // Helper function to categorize subjects
  const categorizeSubjects = (subjects: string[]) => {
    const defaultSubjects = [
      "Mathematics", "English", "Spanish", "Physics", "Chemistry", "Biology", "Literature",
      "English Language", "French", "German", "Italian", "Portuguese", "Chinese", "Japanese", "Korean", "Arabic",
      "Programming", "Business", "Marketing", "Finance", "Data Science", "Web Development", "Graphic Design",
      "Fitness & Bodybuilding", "Cooking", "Music", "Art", "Photography", "Travel", "Conversation"
    ];

    const defaultOnes = subjects.filter(subject => defaultSubjects.includes(subject));
    const approvedOnes = subjects.filter(subject => !defaultSubjects.includes(subject));

    return { defaultOnes, approvedOnes };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8" key={refreshKey}>
      <div className="max-w-4xl mx-auto">
        
        {/* New Tutor - Profile Creation Screen */}
        {isOwnProfile && isTutor && (!tutorData.id || (!currentProfilePictureUrl && !tutorData.bio)) && (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center bg-white rounded-2xl shadow-lg p-12 max-w-md">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Camera className="w-12 h-12 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Create Your Profile
              </h1>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Welcome! Let's set up your tutor profile to start connecting with students and sharing your expertise.
              </p>
              
              <Button 
                onClick={handleCreateProfileClick}
                disabled={hasPendingProfileSubmission || hasPendingProfileUpdate || hasActivePendingUpdate === true || isInitialLoad}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit className="w-6 h-6 mr-3" />
                {hasPendingProfileSubmission ? 'Profile Pending Approval' : 
                 hasPendingProfileUpdate || hasActivePendingUpdate === true ? 'Update Pending' :
                 'Create Profile'}
              </Button>
              
              {/* Guide text when button is blocked */}
              {(hasPendingProfileSubmission || hasPendingProfileUpdate || hasActivePendingUpdate === true) && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800 flex items-center">
                    <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                    Profile update waiting for admin approval.
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    After admin approval, you can edit your profile.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Regular Profile Display */}
        {!(isOwnProfile && isTutor && (!tutorData.id || (!currentProfilePictureUrl && !tutorData.bio))) && (
          <>
        
         {/* Pending Update Notifications - hide during profile creation */}
         {isOwnProfile && isTutor && !skipNextRefresh && (
           <PendingUpdateNotification />
         )}
        {/* Show profile setup notice for own profile without a real database profile */}
        {isOwnProfile && !tutorData.id && !isPendingTutor && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Profile Setup Required</h3>
            <p className="text-yellow-700 mb-3">
              Your tutor profile needs to be set up. Click "Edit Profile" to complete your profile information.
            </p>
             <Button 
               onClick={handleCreateProfileClick}
               disabled={hasPendingProfileSubmission || hasPendingProfileUpdate || hasActivePendingUpdate === true || isInitialLoad}
               className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               <Edit className="w-4 h-4 mr-2" />
               {hasPendingProfileSubmission ? 'Profile Pending Approval' : 
                hasPendingProfileUpdate || hasActivePendingUpdate === true ? 'Update Pending' : 
                'Complete Profile Setup'}
             </Button>
             
             {/* Guide text when button is blocked */}
             {(hasPendingProfileSubmission || hasPendingProfileUpdate || hasActivePendingUpdate === true) && (
               <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded">
                 <p className="text-xs text-red-800 flex items-center">
                   <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                   Profile update waiting for admin approval.
                 </p>
                 <p className="text-xs text-red-700 mt-1">
                   After admin approval, you can edit.
                 </p>
               </div>
             )}
          </div>
        )}

        <Card className="overflow-hidden shadow-lg">
          <CardHeader className="pb-4 bg-gradient-to-r from-blue-500 to-purple-600">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex flex-col items-center gap-4">
                 <div className="relative">
                   <Avatar className="w-32 h-32 border-2 border-white shadow-lg">
                     <AvatarImage 
                       src={currentProfilePictureUrl || '/placeholder.svg'} 
                       alt={tutorData.name || 'Profile'}
                     />
                     <AvatarFallback className="text-2xl bg-white text-blue-600">
                       {tutorData.name?.charAt(0) || 'T'}
                     </AvatarFallback>
                   </Avatar>
                  
                  {/* Country Flag Display - positioned to avoid overlap */}
                  {tutorData.country && (
                    <div className="absolute -bottom-1 -right-1">
                      <TutorCountryFlag 
                        countryCode={tutorData.country} 
                        size="lg"
                        className="bg-white rounded-full p-1 shadow-md border-2 border-white"
                      />
                    </div>
                  )}
                </div>
                
              </div>
              
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                   <div>
                     <div className="flex items-center gap-3">
                       <CardTitle className="text-3xl font-bold text-white">
                         {tutorData.name || 'Tutor'}
                       </CardTitle>
                       {tutorData.country && (
                         <TutorCountryFlag 
                           countryCode={tutorData.country} 
                           showName={true}
                           size="md"
                           className="text-white"
                         />
                       )}
                     </div>
                     <p className="text-lg text-blue-100 mt-1">
                       {tutorData.shortBio || 'Professional Tutor'}
                     </p>
                     <div className="flex items-center mt-2">
                       <div className="flex items-center">
                         {[...Array(5)].map((_, i) => (
                           <Star
                             key={i}
                             className={`w-4 h-4 ${
                               i < Math.floor(averageRating || 0)
                                 ? 'text-yellow-400 fill-current'
                                 : 'text-white/30'
                             }`}
                           />
                         ))}
                         <span className="ml-2 text-sm text-white">
                           {averageRating || 0} ({reviewCount} reviews)
                         </span>
                       </div>
                     </div>
                   </div>
                  
                   <div className="flex flex-col gap-2">
                     {/* Show tutor controls on their own profile */}
                     {isOwnProfile && isTutor && (
                       <>
                           <div className="flex flex-row flex-wrap gap-1.5 sm:gap-2">
                             <Button 
                               onClick={() => {
                                 if (hasActivePendingUpdate === true) {
                                   showPendingNotification('edit');
                                 } else {
                                   setIsEditModalOpen(true);
                                 }
                               }}
                               disabled={hasActivePendingUpdate === true || isInitialLoad}
                               size="sm"
                               className="flex-1 min-w-[90px] bg-card text-primary hover:bg-card/90 shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-xs px-2 py-1.5"
                             >
                               <Edit className="w-3 h-3 mr-0.5 flex-shrink-0" />
                               <span className="text-[10px] sm:text-xs whitespace-nowrap">Edit Profile</span>
                             </Button>

                              <Button
                                onClick={() => setIsChangePasswordModalOpen(true)}
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[90px] bg-card text-primary hover:bg-card/90 border-0 text-xs px-1.5 py-1.5"
                              >
                                <Lock className="w-3 h-3 mr-0.5 flex-shrink-0" />
                                <span className="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis">Password</span>
                              </Button>

                              <Button
                                onClick={() => setIsLearningPlanOpen(true)}
                                size="sm"
                                className="flex-1 min-w-[100px] bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 shadow-md hover:shadow-lg transition-all duration-300 text-xs px-1.5 py-1.5 group overflow-hidden"
                              >
                                <BookOpen className="w-3 h-3 mr-0.5 flex-shrink-0" />
                                <span className="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis">Learning Plan</span>
                                <ArrowRight className="w-3 h-3 ml-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-300" />
                              </Button>

                              <Link to="/settings">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 min-w-[90px] bg-card text-primary hover:bg-card/90 border-0 text-xs px-1.5 py-1.5 overflow-hidden"
                                >
                                  <Bell className="w-3 h-3 mr-0.5 flex-shrink-0" />
                                  <span className="text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis">Push Notification</span>
                                </Button>
                              </Link>
                              
                              {!isPendingTutor && (
                                <>
                                  <Button 
                                    onClick={() => setIsAddStoryModalOpen(true)}
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 min-w-[90px] bg-card text-primary hover:bg-card/90 border-0 text-xs px-2 py-1.5"
                                  >
                                    <Map className="w-3 h-3 mr-0.5 flex-shrink-0" />
                                    <span className="text-[10px] sm:text-xs whitespace-nowrap">Add Story</span>
                                  </Button>
                                  
                                  <Button 
                                    onClick={() => setIsSuspensionModalOpen(true)}
                                    variant="destructive"
                                    size="sm"
                                    className="flex-1 min-w-[80px] text-xs px-1.5 py-1.5"
                                  >
                                    <AlertTriangle className="w-3 h-3 mr-0.5 flex-shrink-0" />
                                    <span className="text-[10px] sm:text-xs whitespace-nowrap">Suspend</span>
                                  </Button>
                                </>
                              )}
                           </div>
                          
                           {/* Guide text when Edit Profile button is blocked */}
                           {hasActivePendingUpdate === true && (
                             <div className="w-full mt-1 p-2 bg-purple-50 border border-purple-200 rounded text-center">
                               <p className="text-xs text-purple-800 flex items-center justify-center">
                                 <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                                 Profile update waiting for admin approval
                               </p>
                               <p className="text-xs text-purple-700 mt-1">
                                 After admin approval, you can edit
                               </p>
                             </div>
                           )}
                           
                      </>
                    )}
                    
                    {/* Show student actions when viewing other tutors' profiles */}
                    {!isOwnProfile && isStudent && (
                      <>
                        <Button className="w-full sm:w-auto">
                          <Calendar className="w-4 h-4 mr-2" />
                          Book a Session
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto">
                          <BookOpen className="w-4 h-4 mr-2" />
                          Start Learning
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto">
                          <Users className="w-4 h-4 mr-2" />
                          Schedule Session
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Send Message
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto">
                          <Heart className="w-4 h-4 mr-2" />
                          Add to Favorites
                        </Button>
                      </>
                    )}

                    {/* Show basic view actions for other tutors viewing profiles */}
                    {!isOwnProfile && isTutor && (
                      <>
                        <Button variant="outline" className="w-full sm:w-auto">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Send Message
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Post Input Section - only for own profile and tutors */}
        {isOwnProfile && isTutor && (
          <div className="mt-6">
            <PostInputSection showBackground={false} />
          </div>
        )}

        {/* Tabbed Content */}
        <div className="mt-6">
          <TutorProfileTabs
            tutorData={tutorData}
            profilePictureUrl={currentProfilePictureUrl}
            isUploadingPicture={isUploadingPicture}
            showProfilePictureSection={showProfilePictureSection}
            setShowProfilePictureSection={setShowProfilePictureSection}
            handleFileUpload={handleFileUpload}
            handleUrlSubmit={handleUrlSubmit}
            setProfilePictureUrl={setProfilePictureUrl}
            profilePictureUrlInput={profilePictureUrl}
            setIsEditModalOpen={setIsEditModalOpen}
            setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
            setIsAddStoryModalOpen={setIsAddStoryModalOpen}
            isOwnProfile={!!isOwnProfile}
            renderExperience={renderExperience}
            getYouTubeVideoId={getYouTubeVideoId}
            categorizeSubjects={categorizeSubjects}
            lessons={lessons}
            onEditSection={handleEditSection}
          />
        </div>

        {/* Keep original card for structure, but hidden */}
        <Card className="hidden">
        </Card>
        
        {/* Close regular profile display wrapper */}
        </>
        )}
      </div>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        tutorData={tutorData}
        onSave={handleProfileSave}
        onShowSuccessModal={handleProfileUpdateSuccess}
        scrollToSection={scrollToSection}
      />

      <ProfileUpdateSuccessModal 
        open={showSuccessModal} 
        onClose={handleSuccessClose} 
      />

      <ScheduleSessionModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
      />

      <AddTravelStoryModal
        isOpen={isAddStoryModalOpen}
        onClose={() => setIsAddStoryModalOpen(false)}
        onSuccess={() => {
          toast({
            title: "Success!",
            description: "Your travel story has been created successfully"
          });
        }}
      />

      <PendingProfileRequestModal
        isOpen={showPendingRequestModal}
        onClose={() => setShowPendingRequestModal(false)}
        requestType={pendingRequestType}
      />

      <PendingProfileSubmissionModal
        isOpen={showPendingSubmissionModal}
        onClose={() => setShowPendingSubmissionModal(false)}
        submissionType={pendingSubmissionType}
        submissionDate={pendingSubmissionType === 'initial' ? pendingSubmissionDate : pendingUpdateDate}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      <ProfileSuspensionModal
        isOpen={isSuspensionModalOpen}
        onClose={() => setIsSuspensionModalOpen(false)}
      />

      {/* Learning Plan Modal */}
      {tutorData?.id && (
        <LearningPlanModal
          isOpen={isLearningPlanOpen}
          onClose={() => setIsLearningPlanOpen(false)}
          tutorId={tutorData.id}
          isOwnProfile={isOwnProfile}
          tutorAbout={tutorData.bio}
        />
      )}

      </div>
    </div>
  );
};

export default TutorProfile;
